import {
  VirtualSmileyConferenceToolClient,
  ServiceInputTypes,
  ServiceOutputTypes,
  $Command,
  VirtualSmileyConferenceToolClientResolvedConfig,
  InvalidInputException,
  AccessDeniedException,
  TcxRuntimeException,
  RequestThrottledException,
  MethodNotAllowedException,
  ResourceConflictException,
  ResourceNotFoundException,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { getApiInvokeUrl } from './ConferencePages/conferenceInfo/utils';
import { MiddlewareStack } from '@smithy/types';
import { getCurrentToken, refreshAuthToken } from './authentication/utils';

const clientConfig = {
  endpoint: getApiInvokeUrl(),
  region: 'us-east-1',
  credentials: {
    accessKeyId: 'elephant',
    secretAccessKey: 'stockings',
  },
};

// Initialize VirtualSmileyConferenceToolClient
export const client = new VirtualSmileyConferenceToolClient(clientConfig);

// SDK auto-retry disabled (maxAttempts: 1) for expensive, non-idempotent ops. The default policy
// retries 5xx, so a 504 from a long RefreshDiffsAgainstPublic would re-run the whole refresh up to
// 3 times. Idempotent reads keep the default retry via `client`.
export const noRetryClient = new VirtualSmileyConferenceToolClient({
  ...clientConfig,
  maxAttempts: 1,
});

// Middleware to add headers
export const addHeaderMiddleware = (
  client: {
    middlewareStack: MiddlewareStack<ServiceInputTypes, ServiceOutputTypes>;
  },
  token: string
): void =>
  client.middlewareStack.add(
    (next) => async (args) => {
      if (
        args.request &&
        typeof args.request === 'object' &&
        'headers' in args.request
      ) {
        Object.assign(
          (args.request as { headers: Record<string, string> }).headers,
          {
            'x-lambda-authorizer-token': token,
          }
        );
      }
      return next(args);
    },
    {
      step: 'build',
      name: 'headersMiddleware',
      tags: ['lambda-auth'],
    }
  );

type SecondTypeOf<T> = T extends $Command<infer _, infer R, infer __>
  ? R
  : never;
type CommandReturns<T> = Promise<SecondTypeOf<T>>;

// Normalizes SDK errors into a simple { statusCode, message } shape.
const normalizeError = (err: any) => {
  if (
    err instanceof InvalidInputException ||
    err instanceof AccessDeniedException ||
    err instanceof TcxRuntimeException ||
    err instanceof RequestThrottledException ||
    err instanceof MethodNotAllowedException ||
    err instanceof ResourceConflictException ||
    err instanceof ResourceNotFoundException
  ) {
    return {
      statusCode: err.$metadata.httpStatusCode ?? 0,
      message: err.message || 'Unknown error occurred',
    };
  }
  // For unknown errors, preserve the status code if it exists
  return {
    statusCode: err.$metadata?.httpStatusCode ?? err.statusCode ?? 0,
    message: err.message || 'Unknown error occurred',
  };
};

const getStatusCode = (err: any): number =>
  err?.$metadata?.httpStatusCode ?? err?.statusCode ?? 0;

// Define a function to call the API
export const callApi = async <
  C extends $Command<any, any, VirtualSmileyConferenceToolClientResolvedConfig>
>(
  command: C,
  token: string,
  opts?: { noRetry?: boolean }
): Promise<any> => {
  // opts.noRetry routes to the maxAttempts:1 client so a 504 is not silently re-fired.
  const sdkClient = opts?.noRetry ? noRetryClient : client;
  // Send the command with the given auth token, (re)installing the auth header
  // middleware so retries pick up a fresh token.
  const send = (authToken: string): CommandReturns<C> => {
    command.middlewareStack.removeByTag('lambda-auth');
    addHeaderMiddleware(command, authToken);
    return sdkClient.send(command);
  };

  // Prefer the live token from the Cognito session so we never send a token
  // that was captured at login and has since expired. Fall back to the passed
  // token (e.g., in tests where no Cognito session exists).
  let lastErr: any;
  try {
    return await send(getCurrentToken() ?? token);
  } catch (err: any) {
    lastErr = err;
  }

  // Safety net: a 403 typically means the token expired (the authorizer returns
  // a Deny policy). Force a session refresh and retry the request once.
  if (getStatusCode(lastErr) === 403) {
    const refreshed = await refreshAuthToken();
    if (refreshed) {
      try {
        return await send(refreshed);
      } catch (retryErr: any) {
        lastErr = retryErr;
      }
    }
  }

  throw normalizeError(lastErr);
};
